import {
  HEADER_ICON,
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownAZ,
  BarChart3,
  Calendar,
  CalendarClock,
  CalendarDays,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  Download,
  Edit,
  Eye,
  EyeOff,
  FileEdit,
  Flag,
  Globe,
  Image,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  ListFilter,
  Loader2,
  MoreVertical,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Target,
  Trash2,
  TrendingUp,
  Users,
  Video,
  Wand2,
  X,
  XCircle,
  Zap,
  ArrowRight,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionDropdown,
  Button,
  DateRangePicker,
  Input,
  Modal,
  PageLayout,
  Pagination,
  Select,
  Tooltip,
} from "../components";
import { AdsCampaignsSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import { cn } from "../utils/cn";
import { getPlatformName, PlatformIcon } from "../utils/platformIcons";

// ─── Constants (outside component to avoid re-creation on every render) ────

const PLATFORM_META: Record<
  string,
  { label: string; dotClass: string; abbr: string; bg: string }
> = {
  facebook: {
    label: "Facebook",
    dotClass: "bg-blue-500",
    abbr: "F",
    bg: "bg-blue-100 text-blue-700",
  },
  instagram: {
    label: "Instagram",
    dotClass: "bg-pink-500",
    abbr: "IG",
    bg: "bg-pink-100 text-pink-700",
  },
  audience_network: {
    label: "Audience Network",
    dotClass: "bg-purple-400",
    abbr: "AN",
    bg: "bg-purple-100 text-purple-700",
  },
  messenger: {
    label: "Messenger",
    dotClass: "bg-blue-400",
    abbr: "M",
    bg: "bg-sky-100 text-sky-700",
  },
  meta: {
    label: "Meta",
    dotClass: "bg-blue-600",
    abbr: "M",
    bg: "bg-blue-100 text-blue-700",
  },
  both: {
    label: "Meta",
    dotClass: "bg-blue-600",
    abbr: "M",
    bg: "bg-blue-100 text-blue-700",
  },
  google_ads: {
    label: "Google Ads",
    dotClass: "bg-yellow-500",
    abbr: "G",
    bg: "bg-yellow-100 text-yellow-700",
  },
  linkedin: {
    label: "LinkedIn",
    dotClass: "bg-blue-700",
    abbr: "in",
    bg: "bg-blue-100 text-blue-800",
  },
  tiktok: {
    label: "TikTok",
    dotClass: "bg-gray-900",
    abbr: "TT",
    bg: "bg-gray-100 text-gray-800",
  },
};

// ─── Types ──────────────────────────────────────────────────────────────────

interface Campaign {
  id: string;
  name: string;
  platform:
    | "facebook"
    | "instagram"
    | "meta"
    | "google_ads"
    | "linkedin"
    | "tiktok"
    | "both";
  status:
    | "active"
    | "paused"
    | "pending"
    | "error"
    | "draft"
    | "completed"
    | "canceled";
  budget: {
    amount: number;
    daily_amount: number;
    currency: string;
    daily_limit?: number;
  };
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpc: number;
  ctr: number;
  roi: number;
  start_date: string;
  end_date?: string;
  created_at: string;
  updated_at?: string;
  thumbnail_url?: string;
  media_type?: string;
  metrics_breakdown?: {
    platform: string;
    conversions: number;
    spent: number;
    impressions: number;
    clicks: number;
  }[];
  post_url?: string;
  status_alert?: { type: string; message: string; at: string } | null;
  form_leads?: number;
  result_type?: "form_leads" | "link_clicks" | "conversions";
  external_campaign_id?: string;
}

interface GroupedCampaign {
  id: string;
  name: string;
  status: Campaign["status"];
  budget: number;
  daily_budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  roi: number;
  thumbnail_url?: string;
  image_url?: string;
  media_type?: string;
  post_url?: string;
  platform: string;
  start_date?: string;
  end_date?: string;
  metrics_breakdown?: Campaign["metrics_breakdown"];
  status_alert?: Campaign["status_alert"];
  form_leads?: number;
  result_type?: Campaign["result_type"];
  children: Campaign[];
}

type SortKey =
  | "date"
  | "end_date"
  | "name"
  | "roi"
  | "spent"
  | "conversions"
  | "impressions";
type SortDir = "asc" | "desc";

// ─── Component ──────────────────────────────────────────────────────────────

// Renders the creative thumbnail, degrading to the placeholder icon when
// there's no URL or the Meta CDN URL fails to load (deleted/expired ad).
// A real component (not inline JSX) so each instance owns its own
// load-error state — needed since this is used inside .map() callbacks.
const CreativeThumb: React.FC<{ src?: string; mediaType?: string }> = ({
  src,
  mediaType,
}) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const isVideo = mediaType && mediaType.toLowerCase().includes("video");

  const isVideoExt =
    src?.toLowerCase().endsWith(".mp4") ||
    src?.toLowerCase().endsWith(".mov") ||
    src?.toLowerCase().endsWith(".webm");

  if (!src || failed) {
    return isVideo || isVideoExt ? (
      <Video className="w-4 h-4 text-gray-300" />
    ) : (
      <Image className="w-4 h-4 text-gray-300" />
    );
  }

  if (isVideoExt) {
    return (
      <video
        src={src}
        className="w-full h-full object-cover"
        muted
        loop
        playsInline
        autoPlay
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <img
      src={src}
      alt={t("Criativo")}
      className="w-full h-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
};

const AdsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeContext } = useActiveContext();

  const STATUS_CONFIG = useMemo<Record<string, { label: string; dot: string }>>(
    () => ({
      active: { label: t("Ativo"), dot: "bg-green-500 border-green-500" },
      paused: { label: t("Pausado"), dot: "border-amber-400" },
      draft: { label: t("Rascunho"), dot: "border-gray-400" },
      error: { label: t("Erro"), dot: "bg-red-500 border-red-500" },
      canceled: { label: t("Cancelado"), dot: "border-red-400" },
      pending: { label: t("Pendente"), dot: "border-yellow-400" },
      completed: { label: t("Finalizado"), dot: "border-blue-400" },
    }),
    [t],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState<
    "all" | "facebook" | "google_ads" | "linkedin" | "tiktok"
  >("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "paused" | "draft" | "error"
  >("all");
  const [datePreset, setDatePreset] = useState("lifetime");
  const [customDateRange, setCustomDateRange] = useState<{
    start: string | undefined;
    end: string | undefined;
  }>({ start: undefined, end: undefined });
  const [sortBy, setSortBy] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [previewGroup, setPreviewGroup] = useState<GroupedCampaign | null>(
    null,
  );
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showZeroData, setShowZeroData] = useState(false);
  const [zeroDataHint, setZeroDataHint] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "cards">(() => {
    if (typeof window === "undefined") return "table";
    return (
      (localStorage.getItem("adsListViewMode") as "table" | "cards") || "table"
    );
  });
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    setIsFiltering(true);
    const t = setTimeout(() => setIsFiltering(false), 250);
    return () => clearTimeout(t);
  }, [
    searchTerm,
    platformFilter,
    statusFilter,
    showZeroData,
    sortBy,
    sortDir,
    viewMode,
  ]);

  useEffect(() => {
    localStorage.setItem("adsListViewMode", viewMode);
  }, [viewMode]);

  // When the user picks a date-restricted preset, pulse the "Mostrar zeradas"
  // checkbox so they know it controls visibility of campaigns with no data.
  useEffect(() => {
    if (datePreset === "lifetime") return;
    setZeroDataHint(true);
    const t = setTimeout(() => setZeroDataHint(false), 3500);
    return () => clearTimeout(t);
  }, [datePreset]);

  // Context tracking — via useEffect to avoid setState during render
  const contextId = `${activeContext.type}-${activeContext.organization?.id ?? ""}-${activeContext.clientCompany?.id ?? ""}`;
  useEffect(() => {
    setCampaigns([]);
    setLoading(true);
    setSelectedRows(new Set());
    setExpandedRows(new Set());
  }, [contextId]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchCampaigns = useCallback(
    async (silent = false) => {
      if (!user?.id) return;
      try {
        if (!silent) setLoading(true);
        else setSyncing(true);
        setError(null);
        if (
          datePreset === "custom" &&
          (!customDateRange.start || !customDateRange.end)
        ) {
          if (!silent) setLoading(false);
          else setSyncing(false);
          return;
        }

        let url = `/api/ads/performance/compare/${user.id}?date_preset=${datePreset}`;

        if (activeContext?.organization?.id) {
          url += `&organization_id=${activeContext.organization.id}`;
        }
        if (activeContext?.clientCompany?.id) {
          url += `&company_id=${activeContext.clientCompany.id}`;
        }

        if (
          datePreset === "custom" &&
          customDateRange.start &&
          customDateRange.end
        ) {
          url += `&start_date=${customDateRange.start}&end_date=${customDateRange.end}`;
        }
        const data = await apiService.get(url);
        if (data) {
          setCampaigns(data.campaigns || []);
          setLastSynced(new Date());
        }
      } catch (err) {
        console.error("Erro ao buscar campanhas:", err);
        if (!silent) {
          setError(
            t(
              "Não foi possível carregar suas campanhas. Tente novamente mais tarde.",
            ),
          );
          showToastError(t("Erro ao carregar campanhas"));
        }
      } finally {
        if (!silent) setLoading(false);
        else setSyncing(false);
      }
    },
    [user?.id, contextId, datePreset, customDateRange],
  );

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Silent polling every 60s
  useEffect(() => {
    const interval = setInterval(() => fetchCampaigns(true), 60000);
    return () => clearInterval(interval);
  }, [fetchCampaigns]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every((id) => selectedRows.has(id));
    setSelectedRows(allSelected ? new Set() : new Set(ids));
  };

  const handleToggleGroupStatus = async (group: GroupedCampaign) => {
    const newAction = group.status === "active" ? "pause" : "resume";
    try {
      await Promise.all(
        group.children.map((c) =>
          apiService.post(`/api/ads/campaigns/${c.id}/${newAction}`),
        ),
      );
      showToastSuccess(
        newAction === "pause"
          ? t("Campanha pausada com sucesso!")
          : t("Campanha ativada com sucesso!"),
      );
      fetchCampaigns(true);
    } catch {
      showToastError(t("Erro ao alterar status da campanha"));
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (
      !window.confirm(
        t(
          "Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.",
        ),
      )
    )
      return;
    try {
      await apiService.delete(`/api/ads/campaigns/${campaignId}`);
      showToastSuccess(t("Campanha excluída com sucesso!"));
      fetchCampaigns(true);
    } catch {
      showToastError(t("Erro ao excluir campanha"));
    }
  };

  const handleBulkPause = async () => {
    const ids = [...selectedRows];
    try {
      await Promise.all(
        ids.map((id) => apiService.post(`/api/ads/campaigns/${id}/pause`)),
      );
      showToastSuccess(`${ids.length} ${t("campanha(s) pausada(s).")}`);
      setSelectedRows(new Set());
      fetchCampaigns(true);
    } catch {
      showToastError(t("Erro ao pausar campanhas"));
    }
  };

  const handleBulkResume = async () => {
    const ids = [...selectedRows];
    try {
      await Promise.all(
        ids.map((id) => apiService.post(`/api/ads/campaigns/${id}/resume`)),
      );
      showToastSuccess(`${ids.length} ${t("campanha(s) ativada(s).")}`);
      setSelectedRows(new Set());
      fetchCampaigns(true);
    } catch {
      showToastError(t("Erro ao ativar campanhas"));
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedRows];
    if (!window.confirm(`${t("Excluir")} ${ids.length} ${t("campanha(s)?")}`))
      return;
    try {
      await Promise.all(
        ids.map((id) => apiService.delete(`/api/ads/campaigns/${id}`)),
      );
      showToastSuccess(`${ids.length} ${t("campanha(s) excluída(s).")}`);
      setSelectedRows(new Set());
      fetchCampaigns(true);
    } catch {
      showToastError(t("Erro ao excluir campanhas"));
    }
  };

  // ── Sorting helpers ────────────────────────────────────────────────────────

  const handleSort = (key: SortKey) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const renderSortIcon = (col: SortKey) => {
    if (sortBy !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return (
      <span className="text-purple-600 ml-1">
        {sortDir === "desc" ? "↓" : "↑"}
      </span>
    );
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const filteredAndSortedCampaigns = useMemo(() => {
    let filtered = Array.isArray(campaigns)
      ? campaigns.filter((c) => {
          const matchesSearch = (c.name || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          const p = c.platform || "";
          const isMetaPlatform = [
            "facebook",
            "instagram",
            "meta",
            "both",
          ].includes(p);
          const matchesPlatform =
            platformFilter === "all" ||
            (platformFilter === "facebook" && isMetaPlatform) ||
            (platformFilter === "google_ads" && p === "google_ads") ||
            (platformFilter === "linkedin" && p === "linkedin") ||
            (platformFilter === "tiktok" && p === "tiktok");
          const matchesStatus =
            statusFilter === "all" || c.status === statusFilter;
          const isBlank = !c.spent && !c.impressions && !c.conversions;
          const matchesZeroData = showZeroData || !isBlank;
          return (
            matchesSearch && matchesPlatform && matchesStatus && matchesZeroData
          );
        })
      : [];

    const statusOrder: Record<string, number> = {
      active: 0,
      pending: 1,
      paused: 2,
      error: 3,
      draft: 4,
      completed: 5,
      canceled: 6,
    };
    const dir = sortDir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      // Campanhas com dados zerados/em branco vão sempre para o final da lista
      const aIsBlank = !a.spent && !a.impressions && !a.conversions;
      const bIsBlank = !b.spent && !b.impressions && !b.conversions;

      if (aIsBlank && !bIsBlank) return 1;
      if (!aIsBlank && bIsBlank) return -1;

      // Always put active campaigns first, regardless of sort key
      const statusDiff =
        (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (statusDiff !== 0) return statusDiff;

      switch (sortBy) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "");
        case "roi":
          return dir * ((a.roi || 0) - (b.roi || 0));
        case "spent":
          return dir * ((a.spent || 0) - (b.spent || 0));
        case "conversions":
          return dir * ((a.conversions || 0) - (b.conversions || 0));
        case "impressions":
          return dir * ((a.impressions || 0) - (b.impressions || 0));
        case "end_date": {
          const tA = a.end_date ? new Date(a.end_date).getTime() : 0;
          const tB = b.end_date ? new Date(b.end_date).getTime() : 0;
          if (tA !== tB) return dir * (tA - tB);
          return dir * (parseInt(a.id) - parseInt(b.id));
        }
        case "date":
        default: {
          const tA = new Date(a.created_at || a.start_date || 0).getTime();
          const tB = new Date(b.created_at || b.start_date || 0).getTime();
          if (tA !== tB) return dir * (tA - tB);
          // Tiebreaker: higher numeric id = more recently created
          return dir * (parseInt(a.id) - parseInt(b.id));
        }
      }
    });
    return filtered;
  }, [
    campaigns,
    searchTerm,
    platformFilter,
    statusFilter,
    showZeroData,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, platformFilter, statusFilter, sortBy, itemsPerPage]);

  const groupedCampaigns = useMemo<GroupedCampaign[]>(() => {
    // Use Map instead of plain object — plain objects with numeric-looking keys
    // are always iterated in ascending numeric order by Object.values(), which
    // would override the sort order from filteredAndSortedCampaigns.
    const groups = new Map<string, GroupedCampaign>();

    filteredAndSortedCampaigns.forEach((campaign) => {
      const key = campaign.id;
      if (!groups.has(key)) {
        groups.set(key, {
          id: campaign.id,
          name: campaign.name || t("Sem Nome"),
          thumbnail_url:
            (campaign as any).thumbnail_url ||
            (campaign as any).image_url ||
            (campaign as any).video_url,
          image_url: (campaign as any).image_url,
          media_type: campaign.media_type,
          status: campaign.status,
          spent: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          budget: 0,
          daily_budget: 0,
          roi: 0,
          platform: campaign.platform,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          metrics_breakdown: campaign.metrics_breakdown,
          post_url: campaign.post_url,
          status_alert: campaign.status_alert,
          form_leads: 0,
          result_type: campaign.result_type || "conversions",
          children: [],
        });
      }
      const group = groups.get(key)!;
      group.children.push(campaign);
      group.spent += campaign.spent || 0;
      group.impressions += campaign.impressions || 0;
      group.clicks += campaign.clicks || 0;
      group.conversions += campaign.conversions || 0;
      group.budget = Math.max(group.budget, campaign.budget?.amount || 0);
      group.daily_budget += campaign.budget?.daily_amount || 0;

      const campaignThumb =
        (campaign as any).thumbnail_url ||
        (campaign as any).image_url ||
        (campaign as any).video_url;
      if (!group.thumbnail_url && campaignThumb) {
        group.thumbnail_url = campaignThumb;
      }
      if (!group.image_url && (campaign as any).image_url) {
        group.image_url = (campaign as any).image_url;
      }
      if (!group.media_type && campaign.media_type) {
        group.media_type = campaign.media_type;
      }
      if (!group.post_url && campaign.post_url)
        group.post_url = campaign.post_url;
      if (campaign.form_leads)
        group.form_leads = (group.form_leads || 0) + campaign.form_leads;
      if (campaign.result_type === "form_leads")
        group.result_type = "form_leads";

      if (campaign.metrics_breakdown?.length) {
        const existing = group.metrics_breakdown || [];
        campaign.metrics_breakdown.forEach((mb) => {
          const idx = existing.findIndex((e) => e.platform === mb.platform);
          if (idx >= 0) {
            existing[idx].spent += mb.spent;
            existing[idx].impressions += mb.impressions;
            existing[idx].clicks += mb.clicks;
            existing[idx].conversions += mb.conversions;
          } else {
            existing.push({ ...mb });
          }
        });
        group.metrics_breakdown = existing;
      }

      if (campaign.status === "active") group.status = "active";
      else if (group.status !== "active" && campaign.status === "paused")
        group.status = "paused";
      else if (
        group.status !== "active" &&
        group.status !== "paused" &&
        campaign.status === "error"
      )
        group.status = "error";
    });

    const result = Array.from(groups.values());
    result.forEach((g) => {
      // If totals are 0 but breakdown has data, derive totals from breakdown
      if (g.metrics_breakdown && g.metrics_breakdown.length > 0) {
        const mbConv = g.metrics_breakdown.reduce(
          (s, r) => s + (r.conversions || 0),
          0,
        );
        const mbSpent = g.metrics_breakdown.reduce(
          (s, r) => s + (r.spent || 0),
          0,
        );
        const mbImpr = g.metrics_breakdown.reduce(
          (s, r) => s + (r.impressions || 0),
          0,
        );
        const mbClicks = g.metrics_breakdown.reduce(
          (s, r) => s + (r.clicks || 0),
          0,
        );
        if (g.conversions === 0 && mbConv > 0) g.conversions = mbConv;
        if (g.spent === 0 && mbSpent > 0) g.spent = mbSpent;
        if (g.impressions === 0 && mbImpr > 0) g.impressions = mbImpr;
        if (g.clicks === 0 && mbClicks > 0) g.clicks = mbClicks;
      }
      g.roi = g.spent > 0 ? g.conversions / g.spent : 0;
    });
    return result;
  }, [filteredAndSortedCampaigns]);

  const currentGroupIds = useMemo(
    () => groupedCampaigns.map((g) => g.id),
    [groupedCampaigns],
  );
  const totalItems = groupedCampaigns.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentGroups = groupedCampaigns.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const dynamicSummary = useMemo(() => {
    const totalSpent = filteredAndSortedCampaigns.reduce(
      (acc, c) => acc + (c.spent || 0),
      0,
    );
    const totalLeads = filteredAndSortedCampaigns.reduce(
      (acc, c) => acc + (c.conversions || 0),
      0,
    );
    const totalImpr = filteredAndSortedCampaigns.reduce(
      (acc, c) => acc + (c.impressions || 0),
      0,
    );
    const activeCnt = filteredAndSortedCampaigns.filter(
      (c) => c.status === "active",
    ).length;
    const avgCPL = totalLeads > 0 ? totalSpent / totalLeads : 0;
    return {
      total: campaigns.length,
      filtered: filteredAndSortedCampaigns.length,
      totalSpent,
      totalLeads,
      totalImpr,
      activeCnt,
      avgCPL,
    };
  }, [campaigns, filteredAndSortedCampaigns]);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderPlatformBadge = (platform: string) => {
    const p = PLATFORM_META[platform] || {
      bg: "bg-gray-100 text-gray-600",
    };
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-sm p-1 shadow-xs",
          p.bg,
        )}
      >
        <PlatformIcon platform={platform} size={12} />
      </span>
    );
  };

  const fmt = (n: number, currency = true) =>
    currency
      ? new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(n)
      : new Intl.NumberFormat("pt-BR").format(n);

  const fmtCPL = (spent: number, conv: number) =>
    conv > 0 ? fmt(spent / conv) : "—";

  // Per-platform metrics breakdown for a grouped campaign — shared by the
  // table's expandable sub-rows and the Cards view's expandable section.
  const getBreakdownRows = (
    group: GroupedCampaign,
  ): NonNullable<Campaign["metrics_breakdown"]> =>
    group.metrics_breakdown && group.metrics_breakdown.length > 0
      ? group.metrics_breakdown
      : Object.values(
          group.children.reduce(
            (
              acc: Record<
                string,
                NonNullable<Campaign["metrics_breakdown"]>[number]
              >,
              child,
            ) => {
              const p = child.platform || "unknown";
              if (!acc[p])
                acc[p] = {
                  platform: p,
                  conversions: 0,
                  spent: 0,
                  impressions: 0,
                  clicks: 0,
                };
              acc[p].conversions += child.conversions || 0;
              acc[p].spent += child.spent || 0;
              acc[p].impressions += child.impressions || 0;
              acc[p].clicks += child.clicks || 0;
              return acc;
            },
            {},
          ),
        );

  // "Ações" menu contents — shared by the table row and the card. Pause/Resume
  // is intentionally left out: it already exists as a one-click inline
  // control (most-used action), so repeating it here would just be noise.
  // Meta Ads Manager só permite deep-link por campanha (não há endpoint público
  // de preview embutível — ver nota em CreativePreviewModal), então "ver na
  // Meta" sempre é um link de saída, nunca um iframe interno.
  const getMetaAdsManagerUrl = (group: GroupedCampaign): string | null => {
    if (!["facebook", "instagram", "meta", "both"].includes(group.platform)) {
      return null;
    }
    const campId = group.children[0]?.external_campaign_id || "";
    // Extracts ID from json string if needed
    let finalId = campId;
    try {
      if (campId.startsWith("{")) {
        const parsed = JSON.parse(campId);
        finalId = parsed.meta_campaign_id || parsed.meta_ad_id || campId;
      }
    } catch (e) {}

    return finalId
      ? `https://adsmanager.facebook.com/adsmanager/manage/campaigns?filter_campaign_ids=${finalId}`
      : `https://adsmanager.facebook.com/adsmanager/manage/campaigns`;
  };

  const getGroupActions = (group: GroupedCampaign) => [
    {
      label: t("Editar"),
      icon: Edit,
      onClick: () => navigate(`/ads/new?draftId=${group.children[0]?.id}`),
    },
    ...(getMetaAdsManagerUrl(group)
      ? [
          {
            label: t("Abrir na Meta ↗"),
            icon: LayoutDashboard,
            onClick: () => window.open(getMetaAdsManagerUrl(group)!, "_blank"),
          },
        ]
      : []),
    {
      label: t("Excluir"),
      icon: Trash2,
      variant: "danger" as const,
      onClick: () => group.children.forEach((c) => handleDelete(c.id)),
    },
  ];

  // Creative preview modal — built on the app's shared Modal (Design System),
  // not a bespoke lightbox. We intentionally do NOT embed Meta's ad in an
  // iframe here: Meta has no public "embed this specific ad" endpoint for
  // third-party apps — the closest official API is
  // `GET /{ad-id}/previews` (Graph API), which needs the ad-level `ad_id`
  // (not the campaign_id this app stores) plus an `ad_format`/placement
  // choice, and issues a fresh Graph API call per view. Neither is available
  // in this app's data model today (only campaign-level IDs are persisted),
  // so wiring it up would mean guessing/faking a preview — exactly the
  // "fragile simulated solution" to avoid. Instead we show the real Meta CDN
  // creative image with a proper loading/error state, plus a genuine
  // "Abrir na Meta" deep link for the authoritative view.
  const CreativePreviewModal: React.FC<{
    group: GroupedCampaign;
    onClose: () => void;
  }> = ({ group, onClose }) => {
    type PreviewState =
      | { mode: "iframe"; url: string; isVertical: boolean }
      | { mode: "image"; url: string }
      | { mode: "none" };

    const [preview, setPreview] = useState<PreviewState | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(true);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);
    const statusCfg = STATUS_CONFIG[group.status] || STATUS_CONFIG.paused;
    const platformCfg = PLATFORM_META[group.platform];
    const metaUrl = getMetaAdsManagerUrl(group);
    const isMetaPlatform = ["facebook", "instagram", "meta", "both"].includes(
      group.platform,
    );

    // Preferência: 1) iframe oficial da Meta (mesmo mecanismo em produção no
    // virtus-leadflow — GET /{ad-id}/previews); 2) imagem do criativo buscada ao
    // vivo (a thumbnail_url cacheada é uma URL assinada da Meta que expira, então
    // nunca confiamos só no valor salvo na listagem); 3) estado de indisponível.
    useEffect(() => {
      let cancelled = false;
      const campaignId = group.children[0]?.id;
      setLoadingPreview(true);
      setPreview(null);
      setImgLoaded(false);
      setImgFailed(false);

      const loadImageFallback = async () => {
        if (!campaignId) return null;
        try {
          const data = await apiService.get(
            `/api/ads/campaigns/${campaignId}/creative-preview`,
          );
          return data?.thumbnail_url || null;
        } catch {
          return null;
        }
      };

      (async () => {
        if (!campaignId) {
          if (!cancelled) {
            setPreview({ mode: "none" });
            setLoadingPreview(false);
          }
          return;
        }

        if (isMetaPlatform) {
          try {
            const iframeData = await apiService.get(
              `/api/ads/campaigns/${campaignId}/ad-preview`,
            );
            if (iframeData?.available && iframeData?.preview_url) {
              if (!cancelled) {
                setPreview({
                  mode: "iframe",
                  url: iframeData.preview_url,
                  isVertical: !!iframeData.is_vertical,
                });
                setLoadingPreview(false);
              }
              return;
            }
          } catch {
            // segue para o fallback de imagem
          }
        }

        const imageUrl = await loadImageFallback();
        if (!cancelled) {
          setPreview(
            imageUrl ? { mode: "image", url: imageUrl } : { mode: "none" },
          );
          setLoadingPreview(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [group.id]);

    return (
      <Modal
        open
        onClose={onClose}
        closeOnOverlayClick
        title={group.name}
        subtitle={
          <span className="inline-flex items-center gap-1.5">
            {platformCfg?.label || getPlatformName(group.platform)}
            <span className="opacity-50">•</span>
            {statusCfg.label}
          </span>
        }
        icon={<Image className="h-5 w-5" />}
        maxWidth="max-w-3xl"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t("Fechar")}
            </Button>
            {metaUrl && (
              <Button
                variant="primary"
                size="sm"
                icon={<LayoutDashboard className="h-4 w-4" />}
                onClick={() => window.open(metaUrl, "_blank")}
                className="bg-linear-to-r from-purple-600 to-indigo-600 border-none"
              >
                {t("Abrir na Meta")}
              </Button>
            )}
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4 md:gap-5 items-start w-full">
          {/* Creative */}
          <div className="flex justify-center md:justify-start">
            {preview?.mode === "iframe" ? (
              <iframe
                src={preview.url}
                title={t("Pré-visualização do anúncio")}
                className="block rounded-xl border-0"
                style={{
                  width: preview.isVertical ? 400 : 520,
                  height: preview.isVertical ? 900 : 840,
                  maxWidth: "100%",
                }}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div className="w-full sm:w-[420px] rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center min-h-[240px] max-h-[50vh]">
                {loadingPreview ? (
                  <div className="w-full h-full min-h-[400px] flex flex-col items-center justify-center bg-gray-100 animate-pulse">
                    <div className="bg-gray-200 rounded-xl p-4 mb-4">
                      <Image className="h-10 w-10 text-gray-300" />
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full w-48 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded-full w-32"></div>
                  </div>
                ) : preview?.mode === "image" && !imgFailed ? (
                  <div className="relative w-full h-full">
                    {!imgLoaded && (
                      <div className="absolute inset-0 w-full h-full min-h-[400px] flex flex-col items-center justify-center bg-gray-100 animate-pulse">
                        <div className="bg-gray-200 rounded-xl p-4 mb-4">
                          <Image className="h-10 w-10 text-gray-300" />
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full w-48 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded-full w-32"></div>
                      </div>
                    )}
                    <img
                      src={preview.url}
                      alt={t("Criativo do anúncio")}
                      className={cn(
                        "w-full h-full max-w-full max-h-[50vh] object-contain",
                        !imgLoaded && "hidden",
                      )}
                      onLoad={() => setImgLoaded(true)}
                      onError={() => setImgFailed(true)}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-400 py-14 px-6 text-center">
                    <div className="bg-white p-4 rounded-full border border-gray-200">
                      <Image className="h-8 w-8 text-gray-300" />
                    </div>
                    <p className="text-sm font-semibold text-gray-500">
                      {t("Criativo indisponível")}
                    </p>
                    <p className="text-xs text-gray-400 max-w-xs">
                      {t(
                        'A prévia não pôde ser carregada — o anúncio pode ter sido removido ou pausado há muito tempo na plataforma. Use "Abrir na Meta" para conferir diretamente por lá.',
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="self-stretch">
            <div className="sticky top-0 grid grid-cols-2 lg:grid-cols-1 gap-3 content-start w-full">
              {/* Resultados */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-4 transition-colors hover:bg-purple-50/50 hover:border-purple-100">
                <div className="bg-purple-100 text-purple-600 p-2.5 rounded-lg shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                    {t("Resultados")}
                  </p>
                  <p className="text-base font-black text-gray-900 mt-0.5">
                    {group.conversions > 0
                      ? fmt(group.conversions, false)
                      : "—"}
                  </p>
                </div>
              </div>

              {/* CPL */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-4 transition-colors hover:bg-blue-50/50 hover:border-blue-100">
                <div className="bg-blue-100 text-blue-600 p-2.5 rounded-lg shrink-0">
                  <Target className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                    {t("CPL")}
                  </p>
                  <p className="text-base font-black text-gray-900 mt-0.5">
                    {fmtCPL(group.spent, group.conversions)}
                  </p>
                </div>
              </div>

              {/* Orçamento */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-4 transition-colors hover:bg-emerald-50/50 hover:border-emerald-100">
                <div className="bg-emerald-100 text-emerald-600 p-2.5 rounded-lg shrink-0">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                    {t("Orçamento (diário)")}
                  </p>
                  <p className="text-base font-black text-gray-900 mt-0.5">
                    {fmt(group.budget)}
                  </p>
                </div>
              </div>

              {/* Valor Usado */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-4 transition-colors hover:bg-amber-50/50 hover:border-amber-100">
                <div className="bg-amber-100 text-amber-600 p-2.5 rounded-lg shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                    {t("Valor usado")}
                  </p>
                  <p className="text-base font-black text-gray-900 mt-0.5">
                    {group.spent > 0 ? fmt(group.spent) : "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  const handleExportCSV = () => {
    if (filteredAndSortedCampaigns.length === 0) {
      showToastError(t("Nenhum dado para exportar."));
      return;
    }

    const headers = [
      t("Nome da Campanha"),
      t("Plataforma"),
      t("Status"),
      t("Gasto (R$)"),
      t("Impressões"),
      t("Cliques"),
      t("Resultados"),
      t("CPL (R$)"),
    ];
    const rows = filteredAndSortedCampaigns.map((g) => [
      g.name || t("Sem Nome"),
      g.platform,
      g.status,
      g.spent.toFixed(2).replace(".", ","),
      g.impressions.toString(),
      g.clicks.toString(),
      g.conversions.toString(),
      g.conversions > 0
        ? (g.spent / g.conversions).toFixed(2).replace(".", ",")
        : "0,00",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"),
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `relatorio_anuncios_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Sync + "Lançar Campanha" — the page's primary actions. Rendered both in
  // the Description Bar (desktop, via PageLayout's `actions` prop, which only
  // renders on lg+ screens) and inline on mobile/tablet where that strip is
  // hidden, so the actions never disappear below the lg breakpoint.
  const renderHeaderActions = (compact: boolean) => (
    <>
      {lastSynced && !compact && (
        <span className="hidden xl:inline text-[10px] text-gray-400 font-medium mr-1">
          {t("Sincronizado")}{" "}
          {lastSynced.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
      <Button
        onClick={handleExportCSV}
        variant="outline"
        size="sm"
        icon={<Download className={HEADER_SECONDARY_ICON} />}
        className={`${HEADER_SECONDARY_BUTTON} shrink-0`}
        title={t("Exportar CSV")}
      >
        <span className="text-xs">{t("Exportar")}</span>
      </Button>
      <Button
        onClick={() => fetchCampaigns(true)}
        disabled={syncing}
        variant="outline"
        size="sm"
        icon={
          <RefreshCw
            className={cn(HEADER_SECONDARY_ICON, syncing && "animate-spin")}
          />
        }
        className={`${HEADER_SECONDARY_BUTTON} shrink-0`}
      >
        <span className="text-xs">{t("Atualizar")}</span>
      </Button>
      <Button
        onClick={() => navigate("/ads/new")}
        variant="primary"
        size="sm"
        icon={<Plus className={HEADER_ICON} />}
        className={`${HEADER_PRIMARY_BUTTON} shrink-0`}
      >
        <span className="hidden sm:inline text-xs">{t("Lançar Campanha")}</span>
        <span className="sm:hidden text-xs">{t("Lançar")}</span>
      </Button>
    </>
  );

  // Cards view — mirrors every field shown in the table (results, leads,
  // CPL, budget, spend, impressions, per-platform breakdown, actions) so
  // switching views never hides information.
  const renderCampaignCard = (group: GroupedCampaign) => {
    const isExpanded = expandedRows.has(group.id);
    const isSelected = selectedRows.has(group.id);
    const statusCfg = STATUS_CONFIG[group.status] || STATUS_CONFIG.paused;
    const platformCfg = PLATFORM_META[group.platform];
    const breakdownRows = getBreakdownRows(group);

    return (
      <div
        className={cn(
          "bg-white border rounded-3xl shadow-xs hover:shadow-lg transition-all p-4 flex flex-col gap-3",
          isSelected
            ? "border-purple-400 ring-2 ring-purple-100"
            : "border-gray-200",
          group.status === "error" && "bg-red-50/30 border-red-200",
        )}
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(group.id)}
            className="w-4 h-4 mt-1 rounded-sm text-purple-600 focus:ring-purple-500 border-gray-300 shrink-0"
          />

          <Tooltip
            content={
              group.image_url || group.thumbnail_url || group.post_url
                ? (() => {
                    const src =
                      group.image_url ||
                      group.thumbnail_url ||
                      group.post_url ||
                      "";
                    const isVid =
                      src.toLowerCase().endsWith(".mp4") ||
                      src.toLowerCase().endsWith(".mov") ||
                      src.toLowerCase().endsWith(".webm");
                    return isVid ? (
                      <video
                        src={src}
                        className="w-[280px] h-[280px] object-contain bg-gray-800 rounded-sm shadow-lg cursor-pointer"
                        muted
                        loop
                        playsInline
                        autoPlay
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewGroup(group);
                        }}
                      />
                    ) : (
                      <img
                        src={src}
                        className="w-[280px] h-[280px] object-contain bg-gray-800 rounded-sm shadow-lg cursor-pointer"
                        alt={group.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewGroup(group);
                        }}
                      />
                    );
                  })()
                : t("Ver criativo do anúncio")
            }
            position="top"
            usePortal
          >
            <div
              className="w-12 h-12 rounded-xl border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50 cursor-pointer hover:opacity-80 hover:scale-105 transition-transform"
              onClick={() => setPreviewGroup(group)}
            >
              <CreativeThumb
                src={group.image_url || group.thumbnail_url || group.post_url}
                mediaType={group.media_type}
              />
            </div>
          </Tooltip>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              {platformCfg && renderPlatformBadge(group.platform)}
              <Tooltip
                content={group.name}
                position="top"
                wrapperClassName="min-w-0 flex-1"
              >
                <span
                  onClick={() => setPreviewGroup(group)}
                  className="text-sm text-gray-800 font-semibold truncate block cursor-pointer hover:text-purple-600 hover:underline transition-colors"
                >
                  {group.name}
                </span>
              </Tooltip>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <div
                className={cn(
                  "w-2 h-2 rounded-full border-2 shrink-0",
                  statusCfg.dot,
                )}
              />
              <span className="text-xs text-gray-500">{statusCfg.label}</span>
            </div>
            {group.status === "error" && (
              <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-semibold mt-1">
                <AlertTriangle className="w-3 h-3" /> {t("Erro de veiculação")}
              </span>
            )}
            {group.status_alert?.type === "auto_paused" && (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 font-semibold mt-1">
                <AlertTriangle className="w-3 h-3" /> {t("Pausada pela Meta")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Tooltip
              content={
                group.status === "active"
                  ? t("Pausar na plataforma")
                  : t("Ativar na plataforma")
              }
              position="top"
            >
              <button
                onClick={() => handleToggleGroupStatus(group)}
                className={cn(
                  "w-7 h-7 rounded-lg flex items-center justify-center transition-all border shadow-xs",
                  group.status === "active"
                    ? "bg-white border-gray-200 text-gray-400 hover:bg-amber-50 hover:border-amber-200 hover:text-amber-600"
                    : "bg-white border-gray-200 text-gray-400 hover:bg-green-50 hover:border-green-200 hover:text-green-600",
                )}
              >
                {group.status === "active" ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5 ml-0.5" />
                )}
              </button>
            </Tooltip>
            <ActionDropdown
              icon={MoreVertical}
              actions={getGroupActions(group)}
              size="sm"
              align="right"
              buttonClassName="border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-purple-600 shadow-xs transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
          {/* Performance */}
          <div className="flex flex-col gap-1">
            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">
              {t("Performance")}
            </span>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-semibold text-gray-800 flex items-baseline gap-1">
                {group.conversions > 0 ? fmt(group.conversions, false) : "—"}
                <span className="text-[10px] font-normal text-gray-500">
                  {group.result_type === "form_leads"
                    ? t("Leads")
                    : group.result_type === "link_clicks"
                      ? t("Cliques")
                      : t("Conv.")}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] font-medium text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded-sm border border-gray-100">
                  {fmtCPL(group.spent, group.conversions)} {t("CPL")}
                </span>
                {group.form_leads && group.form_leads > 0 ? (
                  <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-sm border border-purple-100">
                    {fmt(group.form_leads, false)} {t("Forms")}
                  </span>
                ) : group.result_type === "form_leads" ? (
                  <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-sm border border-red-100">
                    0 {t("Forms")}
                  </span>
                ) : group.conversions > 0 &&
                  group.result_type === "conversions" ? (
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-sm border border-gray-100">
                    {t("Pendente")}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Gastos */}
          <div className="flex flex-col gap-1">
            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">
              {t("Gastos")}
            </span>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-bold text-gray-800">
                {group.spent > 0 ? fmt(group.spent) : "—"}
              </span>
              <span className="text-[10px] text-gray-500">
                {t("Orç.")} {fmt(group.budget)}/{t("dia")}
              </span>
            </div>
          </div>

          {/* Impressões */}
          <div className="flex flex-col gap-1">
            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">
              {t("Impressões")}
            </span>
            <span className="text-sm font-medium text-gray-800">
              {group.impressions > 0 ? fmt(group.impressions, false) : "—"}
            </span>
          </div>
        </div>

        {breakdownRows.length > 0 && (
          <button
            onClick={() => toggleRow(group.id)}
            className="flex items-center gap-1 text-[11px] font-bold text-purple-600 hover:text-purple-700 pt-1"
          >
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
            {t("Detalhes por plataforma")}
          </button>
        )}

        {isExpanded && breakdownRows.length > 0 && (
          <div className="space-y-1.5 pt-1 border-t border-dashed border-gray-100">
            {breakdownRows.map((row, idx) => {
              const bMeta = PLATFORM_META[row.platform] || {
                dotClass: "bg-gray-400",
                label: row.platform,
              };
              return (
                <div
                  key={`${group.id}-c-bk-${idx}`}
                  className="flex items-center justify-between text-xs text-gray-500 bg-gray-50/60 rounded-lg px-2 py-1.5"
                >
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full shrink-0",
                        bMeta.dotClass,
                      )}
                    />
                    {bMeta.label || getPlatformName(row.platform)}
                  </span>
                  <span className="text-right">
                    {row.conversions > 0 ? row.conversions : "—"} {t("res.")} ·{" "}
                    {row.spent > 0 ? fmt(row.spent) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <PageLayout
      title={t("Lista de Anúncios")}
      subtitle={t(
        "Gerencie e acompanhe a performance de todas as suas campanhas pagas criadas na Virtus.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-4"
      loading={loading}
      skeleton={<AdsCampaignsSkeleton />}
      actions={
        <div className="flex items-center gap-2">
          {renderHeaderActions(true)}
        </div>
      }
    >
      <div>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-1">
          {(() => {
            const KPI_COLORS: Record<
              string,
              {
                border: string;
                text: string;
                bg: string;
                iconBg: string;
                iconText: string;
                hoverGlow: string;
                subBg: string;
                subText: string;
              }
            > = {
              indigo: {
                border: "border-indigo-200",
                text: "text-indigo-700",
                bg: "bg-indigo-50/40",
                iconBg: "bg-indigo-100",
                iconText: "text-indigo-600",
                hoverGlow: "hover:shadow-[0_4px_24px_rgba(99,102,241,0.18)]",
                subBg: "bg-indigo-50",
                subText: "text-indigo-500",
              },
              purple: {
                border: "border-purple-200",
                text: "text-purple-700",
                bg: "bg-purple-50/40",
                iconBg: "bg-purple-100",
                iconText: "text-purple-600",
                hoverGlow: "hover:shadow-[0_4px_24px_rgba(168,85,247,0.18)]",
                subBg: "bg-purple-50",
                subText: "text-purple-500",
              },
              green: {
                border: "border-green-200",
                text: "text-green-700",
                bg: "bg-green-50/40",
                iconBg: "bg-green-100",
                iconText: "text-green-600",
                hoverGlow: "hover:shadow-[0_4px_24px_rgba(34,197,94,0.18)]",
                subBg: "bg-green-50",
                subText: "text-green-500",
              },
              amber: {
                border: "border-amber-200",
                text: "text-amber-700",
                bg: "bg-amber-50/40",
                iconBg: "bg-amber-100",
                iconText: "text-amber-600",
                hoverGlow: "hover:shadow-[0_4px_24px_rgba(245,158,11,0.18)]",
                subBg: "bg-amber-50",
                subText: "text-amber-500",
              },
            };

            return [
              {
                icon: DollarSign,
                color: "indigo",
                label: t("Total Investido"),
                value: fmt(dynamicSummary.totalSpent),
                sub: t("Gasto total em campanhas"),
              },
              {
                icon: Users,
                color: "purple",
                label: t("Leads Gerados"),
                value: fmt(dynamicSummary.totalLeads, false),
                sub: t("Conversões capturadas"),
              },
              {
                icon: Zap,
                color: "green",
                label: t("CPL Médio"),
                value: fmt(dynamicSummary.avgCPL),
                sub: t("Custo por lead"),
              },
              {
                icon: LayoutDashboard,
                color: "amber",
                label: t("Ativas"),
                value: String(dynamicSummary.activeCnt),
                sub: t("Campanhas rodando agora"),
                onClick: () => {
                  setStatusFilter("active");
                  setShowZeroData(true);
                },
              },
            ].map(({ icon: Icon, color, label, value, sub, onClick }) => {
              const s = KPI_COLORS[color];
              return (
                <div
                  key={label}
                  onClick={onClick}
                  className={cn(
                    "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 flex flex-col gap-3 overflow-hidden group",
                    s.border,
                    s.hoverGlow,
                    onClick
                      ? "cursor-pointer hover:-translate-y-0.5"
                      : "hover:-translate-y-0.5",
                  )}
                >
                  {/* Colored top accent bar */}
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 h-[3px]",
                      s.iconBg,
                    )}
                  />

                  <div className="flex items-start justify-between pt-0.5">
                    <div className="flex flex-col min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                        {label}
                      </p>
                      <h3
                        className={cn(
                          "text-2xl font-black tracking-tight leading-none",
                          s.text,
                        )}
                      >
                        {value}
                      </h3>
                    </div>
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110",
                        s.iconBg,
                        s.iconText,
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>

                  <div
                    className={cn(
                      "flex items-center justify-between gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg",
                      s.subBg,
                      s.subText,
                    )}
                  >
                    <div className="flex items-center gap-1 truncate">
                      <BarChart3 className="h-3 w-3 shrink-0" />
                      <span className="truncate">{sub}</span>
                    </div>
                    {onClick && (
                      <ArrowRight className="h-3 w-3 shrink-0 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Action Bar - full-bleed sticky filter bar, same pattern as the "Postagens" page */}
        <div className="sticky top-0 z-30 -mx-4 px-4 py-1.5 mb-4 bg-white/95 backdrop-blur-md border-b border-purple-200 flex flex-col gap-3">
          <div className="flex flex-row flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px] md:min-w-[250px]">
              <Input
                type="text"
                placeholder={t("Buscar campanhas por nome...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                inputSize="sm"
                icon={<Search className="h-4 w-4 text-gray-400" />}
                className="w-full h-8! sm:h-9!"
              />
            </div>

            <div className="w-[140px] sm:w-[195px] shrink-0">
              <Select
                value={platformFilter}
                onChange={(v) => setPlatformFilter(v as any)}
                options={[
                  {
                    value: "all",
                    label: t("Todas as Redes"),
                    icon: <Globe />,
                  },
                  {
                    value: "facebook",
                    label: t("Meta (FB/IG)"),
                    icon: <PlatformIcon platform="facebook" />,
                  },
                  {
                    value: "google_ads",
                    label: t("Google Ads"),
                    icon: <PlatformIcon platform="google_ads" />,
                  },
                  {
                    value: "linkedin",
                    label: t("LinkedIn Ads"),
                    icon: <PlatformIcon platform="linkedin" />,
                  },
                  {
                    value: "tiktok",
                    label: t("TikTok Ads"),
                    icon: <PlatformIcon platform="tiktok" />,
                  },
                ]}
                size="sm"
              />
            </div>

            <div className="w-[150px] sm:w-[180px] shrink-0">
              <Select
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as any)}
                options={[
                  {
                    value: "all",
                    label: t("Todos Status"),
                    icon: <ListFilter />,
                  },
                  { value: "active", label: t("Ativas"), icon: <Play /> },
                  { value: "paused", label: t("Pausadas"), icon: <Pause /> },
                  { value: "pending", label: t("Pendentes"), icon: <Clock /> },
                  { value: "draft", label: t("Rascunho"), icon: <FileEdit /> },
                  {
                    value: "completed",
                    label: t("Finalizadas"),
                    icon: <CheckCircle />,
                  },
                  {
                    value: "canceled",
                    label: t("Canceladas"),
                    icon: <XCircle />,
                  },
                  {
                    value: "error",
                    label: t("Com erro"),
                    icon: <AlertTriangle />,
                  },
                ]}
                size="sm"
              />
            </div>

            <div className="w-[135px] sm:w-[175px] shrink-0">
              <Select
                value={datePreset}
                onChange={(v) => setDatePreset(v as string)}
                options={[
                  { value: "lifetime", label: t("Vitalício"), icon: <Globe /> },
                  { value: "today", label: t("Hoje"), icon: <Calendar /> },
                  { value: "yesterday", label: t("Ontem"), icon: <Clock /> },
                  {
                    value: "last_7d",
                    label: t("Últimos 7 dias"),
                    icon: <CalendarDays />,
                  },
                  {
                    value: "last_30d",
                    label: t("Últimos 30 dias"),
                    icon: <CalendarDays />,
                  },
                  {
                    value: "this_month",
                    label: t("Este mês"),
                    icon: <CalendarClock />,
                  },
                  {
                    value: "last_month",
                    label: t("Mês passado"),
                    icon: <CalendarClock />,
                  },
                  {
                    value: "custom",
                    label: t("Data específica"),
                    icon: <CalendarDays />,
                  },
                ]}
                size="sm"
              />
            </div>

            {datePreset === "custom" && (
              <div className="w-[220px] shrink-0">
                <DateRangePicker
                  value={customDateRange}
                  onChange={setCustomDateRange}
                  label={t("Período")}
                />
              </div>
            )}

            <div className="w-[150px] sm:w-[195px] shrink-0">
              <Select
                value={sortBy}
                onChange={(v) => {
                  setSortBy(v as SortKey);
                  setSortDir("desc");
                }}
                options={[
                  { value: "date", label: t("Mais recentes"), icon: <Clock /> },
                  {
                    value: "end_date",
                    label: t("Data de término"),
                    icon: <Flag />,
                  },
                  {
                    value: "name",
                    label: t("Nome (A-Z)"),
                    icon: <ArrowDownAZ />,
                  },
                  {
                    value: "spent",
                    label: t("Maior gasto"),
                    icon: <DollarSign />,
                  },
                  {
                    value: "conversions",
                    label: t("Mais leads"),
                    icon: <Sparkles />,
                  },
                  {
                    value: "impressions",
                    label: t("Mais impressões"),
                    icon: <TrendingUp />,
                  },
                ]}
                size="sm"
              />
            </div>

            <div className="shrink-0 flex items-center gap-2">
              <Tooltip
                content={
                  zeroDataHint
                    ? t("Ative para ver campanhas sem atividade neste período")
                    : t("Mostrar campanhas sem métricas de gastos/impressões")
                }
                position="top"
              >
                <button
                  type="button"
                  aria-label={t("Mostrar zeradas")}
                  aria-pressed={showZeroData}
                  onClick={() => {
                    setShowZeroData(!showZeroData);
                    setZeroDataHint(false);
                  }}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl transition-all border",
                    showZeroData
                      ? "bg-purple-50 text-purple-700 border-purple-200 shadow-xs"
                      : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700",
                    zeroDataHint &&
                      !showZeroData &&
                      "ring-2 ring-purple-400 ring-offset-1 border-transparent animate-pulse",
                  )}
                >
                  {showZeroData ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </button>
              </Tooltip>
            </div>

            {/* Table / Cards view toggle */}
            <div
              role="group"
              aria-label={t("Modo de visualização")}
              className="shrink-0 flex items-center gap-1 bg-gray-100 rounded-xl p-1 border border-purple-200"
            >
              <Tooltip content={t("Visualização em tabela")} position="top">
                <button
                  onClick={() => setViewMode("table")}
                  aria-label={t("Visualização em tabela")}
                  aria-pressed={viewMode === "table"}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-lg transition-all",
                    viewMode === "table"
                      ? "bg-white text-purple-700 shadow-xs ring-1 ring-purple-200"
                      : "text-gray-400 hover:text-gray-600 hover:bg-white/60",
                  )}
                >
                  <LayoutList className="h-4 w-4" />
                </button>
              </Tooltip>
              <Tooltip content={t("Visualização em cards")} position="top">
                <button
                  onClick={() => setViewMode("cards")}
                  aria-label={t("Visualização em cards")}
                  aria-pressed={viewMode === "cards"}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-lg transition-all",
                    viewMode === "cards"
                      ? "bg-white text-purple-700 shadow-xs ring-1 ring-purple-200"
                      : "text-gray-400 hover:text-gray-600 hover:bg-white/60",
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>

            {/* Primary actions live in the Description Bar on lg+ (see PageLayout's
              `actions` prop above); that strip is hidden below lg, so repeat a
              compact version here for mobile/tablet. */}
            <div className="shrink-0 flex items-center gap-2 lg:hidden">
              {renderHeaderActions(true)}
            </div>
          </div>

          {campaigns.length > 0 &&
            (platformFilter !== "all" ||
              statusFilter !== "all" ||
              searchTerm) && (
              <div className="pt-3 border-t border-purple-100 flex gap-2 items-center justify-between text-xs text-gray-600">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    {t("Exibindo")}{" "}
                    <strong className="text-gray-900">
                      {dynamicSummary.filtered}
                    </strong>{" "}
                    {t("de")} {dynamicSummary.total} {t("campanhas")}
                  </span>

                  {/* Divider */}
                  <div className="w-px h-3 bg-gray-300 hidden sm:block mx-1" />

                  {/* Badges */}
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Busca")}: {searchTerm}
                      <button
                        onClick={() => setSearchTerm("")}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {platformFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Plat.")}:{" "}
                      {platformFilter === "facebook"
                        ? "Meta"
                        : platformFilter === "google_ads"
                          ? "Google"
                          : platformFilter === "linkedin"
                            ? "LinkedIn"
                            : "TikTok"}
                      <button
                        onClick={() => setPlatformFilter("all")}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {statusFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                      {t("Status")}:{" "}
                      {STATUS_CONFIG[statusFilter]?.label || statusFilter}
                      <button
                        onClick={() => setStatusFilter("all")}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>

                <button
                  onClick={() => {
                    setSearchTerm("");
                    setPlatformFilter("all");
                    setStatusFilter("all");
                    setShowZeroData(true);
                  }}
                  className="text-purple-600 hover:text-purple-700 font-black uppercase tracking-tighter shrink-0"
                >
                  {t("Limpar filtros")}
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p className="text-sm font-medium flex-1">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchCampaigns()}
            className="text-red-700 hover:bg-red-100 h-8 px-3 rounded-xl font-bold text-xs"
          >
            {t("Tentar Novamente")}
          </Button>
          <button
            onClick={() => setError(null)}
            className="p-1 hover:bg-red-100 rounded-lg transition-colors text-red-400 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Auto-pause alerts */}
      {groupedCampaigns.some((g) => g.status_alert?.type === "auto_paused") && (
        <div className="mb-4 py-2.5 px-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <p className="text-[11px] sm:text-xs text-amber-800 leading-tight">
            <strong>{t("Atenção")}:</strong>{" "}
            {t(
              "Algumas campanhas foram pausadas automaticamente pela Meta. Verifique o motivo no Gerenciador de Anúncios.",
            )}
          </p>
        </div>
      )}

      {/* Table */}
      <div
        className={cn(
          "min-h-[400px] flex flex-col",
          viewMode === "table" ||
            loading ||
            filteredAndSortedCampaigns.length === 0
            ? "bg-white/50 backdrop-blur-xs border border-purple-200 rounded-3xl shadow-xl shadow-purple-500/5 overflow-hidden"
            : "gap-4",
        )}
      >
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-purple-600">
            <Loader2 className="h-10 w-10 animate-spin mb-4" />
            <p className="font-bold animate-pulse tracking-tight">
              {t("Sincronizando campanhas...")}
            </p>
          </div>
        ) : filteredAndSortedCampaigns.length > 0 ? (
          viewMode === "cards" ? (
            <div
              className={cn(
                "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 transition-all duration-300",
                isFiltering &&
                  "opacity-40 blur-[1px] pointer-events-none scale-[0.99]",
              )}
            >
              {currentGroups.map((group) => (
                <React.Fragment key={group.id}>
                  {renderCampaignCard(group)}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div
              className={cn(
                "overflow-x-auto transition-all duration-300",
                isFiltering && "opacity-40 blur-[1px] pointer-events-none",
              )}
            >
              <table className="w-full text-left border-collapse table-fixed">
                <thead>
                  <tr className="bg-white border-b border-gray-200">
                    <th className="w-10 px-3 py-3">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={
                            currentGroupIds.length > 0 &&
                            currentGroupIds.every((id) => selectedRows.has(id))
                          }
                          onChange={() => toggleSelectAll(currentGroupIds)}
                          className="w-4 h-4 rounded-sm text-purple-600 focus:ring-purple-500 border-gray-300"
                        />
                      </div>
                    </th>
                    <th className="w-[300px] px-2 py-3">
                      <button
                        onClick={() => handleSort("name")}
                        className="text-xs font-bold text-gray-600 hover:text-purple-600 flex items-center"
                      >
                        {t("Anúncio")} {renderSortIcon("name")}
                      </button>
                    </th>
                    <th className="px-4 py-3 w-[180px]">
                      <button
                        onClick={() => handleSort("conversions")}
                        className="text-xs font-bold text-gray-600 hover:text-purple-600 flex items-center ml-auto"
                      >
                        {t("Performance")} {renderSortIcon("conversions")}
                      </button>
                    </th>
                    <th className="px-4 py-3 w-[140px]">
                      <button
                        onClick={() => handleSort("spent")}
                        className="text-xs font-bold text-gray-600 hover:text-purple-600 flex items-center ml-auto"
                      >
                        {t("Gastos")} {renderSortIcon("spent")}
                      </button>
                    </th>
                    <th className="px-4 py-3 w-[120px]">
                      <button
                        onClick={() => handleSort("impressions")}
                        className="text-xs font-bold text-gray-600 hover:text-purple-600 flex items-center ml-auto"
                      >
                        {t("Impressões")} {renderSortIcon("impressions")}
                      </button>
                    </th>
                    <th className="w-[100px] px-2 py-3 text-center text-xs font-bold text-gray-600">
                      {t("Ações")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {currentGroups.map((group) => {
                    const isExpanded = expandedRows.has(group.id);
                    const isSelected = selectedRows.has(group.id);
                    const statusCfg =
                      STATUS_CONFIG[group.status] || STATUS_CONFIG.paused;
                    const platformCfg = PLATFORM_META[group.platform];

                    const breakdownRows = getBreakdownRows(group);

                    return (
                      <React.Fragment key={group.id}>
                        <tr
                          className={cn(
                            "group/row transition-colors border-l-4",
                            isExpanded
                              ? "bg-blue-50/20 border-blue-400"
                              : "hover:bg-gray-50 border-transparent",
                            isSelected ? "bg-purple-50/40" : "",
                            group.status === "error" ? "bg-red-50/30" : "",
                          )}
                        >
                          {/* Checkbox */}
                          <td className="w-10 px-3 py-4">
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(group.id)}
                                className="w-4 h-4 rounded-sm text-purple-600 focus:ring-purple-500 border-gray-300"
                              />
                            </div>
                          </td>

                          {/* Anúncio */}
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleRow(group.id)}
                                className="p-0.5 hover:bg-gray-200 rounded-sm text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                              {/* Thumbnail */}
                              <Tooltip
                                content={
                                  group.image_url ||
                                  group.thumbnail_url ||
                                  group.post_url
                                    ? (() => {
                                        const src =
                                          group.image_url ||
                                          group.thumbnail_url ||
                                          group.post_url ||
                                          "";
                                        const isVid =
                                          src.toLowerCase().endsWith(".mp4") ||
                                          src.toLowerCase().endsWith(".mov") ||
                                          src.toLowerCase().endsWith(".webm");
                                        return isVid ? (
                                          <video
                                            src={src}
                                            className="w-[280px] h-[280px] object-contain bg-gray-800 rounded-sm shadow-lg cursor-pointer"
                                            muted
                                            loop
                                            playsInline
                                            autoPlay
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPreviewGroup(group);
                                            }}
                                          />
                                        ) : (
                                          <img
                                            src={src}
                                            className="w-[280px] h-[280px] object-contain bg-gray-800 rounded-sm shadow-lg cursor-pointer"
                                            alt={group.name}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPreviewGroup(group);
                                            }}
                                          />
                                        );
                                      })()
                                    : t("Ver criativo do anúncio")
                                }
                                position="top"
                                usePortal
                              >
                                <div
                                  className="w-10 h-10 rounded-lg border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50 transition-all cursor-pointer hover:opacity-80 hover:shadow-md hover:scale-105"
                                  onClick={() => setPreviewGroup(group)}
                                >
                                  <CreativeThumb
                                    src={
                                      group.image_url ||
                                      group.thumbnail_url ||
                                      group.post_url
                                    }
                                    mediaType={group.media_type}
                                  />
                                </div>
                              </Tooltip>

                              {/* Name + platform badge */}
                              <div className="min-w-0 flex-1 flex flex-col gap-0.5 justify-center">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {platformCfg &&
                                    renderPlatformBadge(group.platform)}
                                  <Tooltip
                                    content={group.name}
                                    position="top"
                                    wrapperClassName="min-w-0 flex-1"
                                  >
                                    <span
                                      onClick={() => setPreviewGroup(group)}
                                      className="text-sm text-gray-800 font-medium truncate block cursor-pointer hover:text-purple-600 hover:underline transition-colors"
                                    >
                                      {group.name}
                                    </span>
                                  </Tooltip>
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <div
                                    className={cn(
                                      "w-2 h-2 rounded-full border-2 shrink-0",
                                      statusCfg.dot,
                                    )}
                                  />
                                  <span className="text-[10px] text-gray-500 font-medium">
                                    {statusCfg.label}
                                  </span>
                                  {group.status === "error" && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] text-red-600 font-semibold ml-1">
                                      <AlertTriangle className="w-2.5 h-2.5" />{" "}
                                      {t("Erro")}
                                    </span>
                                  )}
                                  {group.status_alert?.type ===
                                    "auto_paused" && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-semibold ml-1">
                                      <AlertTriangle className="w-2.5 h-2.5" />{" "}
                                      {t("Pausada")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Performance */}
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm text-gray-800 font-bold flex items-center justify-end gap-1">
                              {group.conversions > 0
                                ? fmt(group.conversions, false)
                                : "—"}
                              <span className="text-xs font-normal text-gray-500">
                                {group.result_type === "form_leads"
                                  ? t("Leads")
                                  : group.result_type === "link_clicks"
                                    ? t("Cliques")
                                    : t("Conv.")}
                              </span>
                            </div>
                            <div className="flex items-center justify-end gap-1.5 mt-1">
                              <span className="text-[10px] font-medium text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded-sm border border-gray-100">
                                {fmtCPL(group.spent, group.conversions)}{" "}
                                {t("CPL")}
                              </span>
                              {group.form_leads && group.form_leads > 0 ? (
                                <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-sm border border-purple-100">
                                  {fmt(group.form_leads, false)} {t("Forms")}
                                </span>
                              ) : group.result_type === "form_leads" ? (
                                <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-sm border border-red-100">
                                  0 {t("Forms")}
                                </span>
                              ) : group.conversions > 0 &&
                                group.result_type === "conversions" ? (
                                <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-sm border border-gray-100">
                                  {t("Pendente")}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          {/* Gastos */}
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm text-gray-800 font-bold">
                              {group.spent > 0 ? fmt(group.spent) : "—"}
                            </div>
                            <div className="text-[10px] text-gray-500 mt-1">
                              {t("Orç.")} {fmt(group.budget)}/{t("dia")}
                            </div>
                          </td>

                          {/* Impressões */}
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm text-gray-800 font-medium">
                              {group.impressions > 0
                                ? fmt(group.impressions, false)
                                : "—"}
                            </div>
                          </td>

                          {/* Ações */}
                          <td className="px-2 py-3 align-middle">
                            <div className="flex h-full items-center justify-center gap-2">
                              {/* Pause / Resume button */}
                              <Tooltip
                                content={
                                  group.status === "active"
                                    ? t("Pausar na plataforma")
                                    : t("Ativar na plataforma")
                                }
                                position="top"
                              >
                                <button
                                  onClick={() => handleToggleGroupStatus(group)}
                                  className={cn(
                                    "shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all border shadow-xs",
                                    group.status === "active"
                                      ? "bg-white border-gray-200 text-gray-500 hover:bg-amber-50 hover:border-amber-200 hover:text-amber-600"
                                      : "bg-white border-gray-200 text-gray-500 hover:bg-green-50 hover:border-green-200 hover:text-green-600",
                                  )}
                                >
                                  {group.status === "active" ? (
                                    <Pause className="w-4 h-4" />
                                  ) : (
                                    <Play className="w-4 h-4 ml-0.5" />
                                  )}
                                </button>
                              </Tooltip>

                              <ActionDropdown
                                icon={MoreVertical}
                                actions={getGroupActions(group)}
                                size="sm"
                                buttonClassName="border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-purple-600 shadow-xs transition-all"
                              />
                            </div>
                          </td>
                        </tr>

                        {/* Breakdown rows */}
                        {isExpanded &&
                          breakdownRows.map((row, idx) => {
                            const bMeta = PLATFORM_META[row.platform] || {
                              dotClass: "bg-gray-400",
                              label: row.platform,
                            };
                            return (
                              <tr
                                key={`${group.id}-bk-${idx}`}
                                className="bg-gray-50/60 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                              >
                                <td />
                                <td className="px-2 py-2 pl-16">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={cn(
                                        "w-2 h-2 rounded-full shrink-0",
                                        bMeta.dotClass,
                                      )}
                                    />
                                    <span className="text-xs text-gray-600 font-medium">
                                      {bMeta.label ||
                                        getPlatformName(row.platform)}
                                    </span>
                                  </div>
                                </td>
                                {/* Performance breakdown */}
                                <td className="px-4 py-2 text-right">
                                  <div className="text-xs text-gray-600 font-semibold">
                                    {row.conversions > 0
                                      ? row.conversions
                                      : "—"}
                                  </div>
                                  <div className="flex items-center justify-end gap-1.5 mt-0.5">
                                    <span className="text-[9px] text-gray-500">
                                      {fmtCPL(row.spent, row.conversions)}
                                    </span>
                                    {(row as any).form_leads > 0 && (
                                      <span className="text-[9px] text-purple-600 font-medium bg-purple-50 px-1 rounded-sm">
                                        {(row as any).form_leads} {t("Forms")}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {/* Gastos breakdown */}
                                <td className="px-4 py-2 text-right">
                                  <div className="text-xs text-gray-600 font-medium">
                                    {row.spent > 0 ? fmt(row.spent) : "—"}
                                  </div>
                                </td>
                                {/* Impressões breakdown */}
                                <td className="px-4 py-2 text-right text-xs text-gray-500">
                                  {row.impressions > 0
                                    ? fmt(row.impressions, false)
                                    : "—"}
                                </td>
                                <td />
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center py-20 px-6 text-center border-2 border-dashed border-gray-200 rounded-3xl bg-gray-50/50 m-6 transition-all hover:bg-gray-50">
            <div className="bg-white p-5 rounded-2xl shadow-xs border border-gray-100 mb-5 relative group">
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-400 rounded-full animate-ping opacity-75" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full" />
              <LayoutDashboard className="h-10 w-10 text-purple-600 group-hover:scale-110 transition-transform duration-300" />
            </div>
            <h3 className="text-gray-900 font-bold text-xl mb-2">
              {campaigns.length === 0
                ? t("Nenhuma campanha rodando ainda")
                : t("Nenhum resultado encontrado")}
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto mb-8 leading-relaxed">
              {campaigns.length === 0
                ? t(
                    "Impulsione seu negócio criando sua primeira campanha publicitária multiplataforma em poucos cliques.",
                  )
                : t(
                    "Seus filtros atuais não retornaram nenhuma campanha. Tente ajustar os parâmetros de busca.",
                  )}
            </p>
            {campaigns.length === 0 ? (
              <Button
                onClick={() => navigate("/ads/new")}
                variant="primary"
                icon={<Plus className="h-5 w-5" />}
                className="rounded-2xl shadow-xl shadow-purple-500/25 bg-linear-to-r from-purple-600 to-indigo-600 border-none font-semibold px-8 py-3"
              >
                {t("Criar minha primeira campanha")}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setPlatformFilter("all");
                  setStatusFilter("all");
                  setShowZeroData(true);
                }}
                variant="secondary"
                icon={<RefreshCw className="h-4 w-4" />}
                className="rounded-xl border-gray-200 shadow-xs hover:bg-gray-100 bg-white"
              >
                {t("Limpar todos os filtros")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredAndSortedCampaigns.length > 0 && (
        <div className="mt-4 bg-white rounded-2xl animate-in fade-in slide-in-from-bottom-2 duration-500">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      )}

      {/* Bulk action bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-purple-400" />
            <span className="text-sm font-bold">
              {selectedRows.size}{" "}
              {selectedRows.size > 1 ? t("selecionados") : t("selecionado")}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs bg-gray-800 border-gray-700 hover:bg-gray-700"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("token");
                  const res = await fetch("/api/ads/fix-old-campaigns", {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const data = await res.json();
                  alert(data.message);
                  window.location.reload();
                } catch (e) {
                  alert("Erro ao rodar script");
                }
              }}
            >
              <Wand2 className="h-3 w-3 mr-2" />
              {t("Corrigir Imagens Antigas")}
            </Button>
          </div>
          <div className="w-px h-5 bg-gray-700" />
          <button
            onClick={handleBulkResume}
            className="text-sm text-gray-200 hover:text-green-400 flex items-center gap-1.5 transition-colors"
          >
            <Play className="h-3.5 w-3.5" /> {t("Ativar")}
          </button>
          <button
            onClick={handleBulkPause}
            className="text-sm text-gray-200 hover:text-amber-400 flex items-center gap-1.5 transition-colors"
          >
            <Pause className="h-3.5 w-3.5" /> {t("Pausar")}
          </button>
          <button
            onClick={handleBulkDelete}
            className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1.5 transition-colors ml-2"
          >
            <Trash2 className="h-3.5 w-3.5" /> {t("Excluir")}
          </button>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="text-gray-500 hover:text-gray-300 transition-colors ml-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Creative preview — Design System Modal, not an ad-hoc lightbox */}
      {previewGroup && (
        <CreativePreviewModal
          group={previewGroup}
          onClose={() => setPreviewGroup(null)}
        />
      )}
    </PageLayout>
  );
};

export default AdsList;
